<template>
  <div class="lr-payment">
    <!-- Payment type selection -->
    <div class="payment-type-selector">
      <button
        type="button"
        v-on:click="selectPaymentType('wwf_purchasetokenise')"
        :class="{ selected: isCreditCard }"
      >
        <span>Donate by Credit Card</span>
      </button>
      <button
        type="button"
        v-on:click="selectPaymentType('wwf_paypal')"
        :class="{ selected: isPayPal }"
      >
        <span>Donate by Paypal</span>
      </button>
      <button
        type="button"
        v-on:click="selectPaymentType('wwf_directdebit')"
        :class="{ selected: isDirectDebit }"
      >
        <span>Donate by Direct Debit</span>
      </button>
    </div>

    <!-- Summary -->
    <div class="summary-container">
      <div class="summary">
        <div class="amount">${{ donation.amount }}</div>
        <div class="secure">
          <img src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/2940/dist/assets/img/secure-icon.svg" />
          <div class="text">
            <p>Secure</p>
            <p>payment</p>
          </div>
        </div>
      </div>
      <hr />
    </div>

    <!-- Payment form -->
    <div class="payment-form">
      <CreditCard
        @success="processSuccess"
        @retry="retry"
        v-show="isCreditCard"
        ref="creditCardCheckout"
      ></CreditCard>
      <DirectDebit
        @success="processSuccess"
        @retry="retry"
        v-show="isDirectDebit"
        ref="directDebitCheckout"
      ></DirectDebit>
      <div class="paypal-payment" v-show="isPayPal">
        <a
          class="paypal-button"
          v-on:click="checkoutPaypal"
          v-if="!isLoading"
        ></a>
        <div v-if="isLoading">
          <i class="far fa-spinner-third fa-spin"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CreditCard from "./../donation/CreditCard";
import DirectDebit from "./../donation/DirectDebit";

import { mapState } from "vuex";
import gtm from "./../../utils/gtm";
import { getNameByDonationType, getNameByPaymentType } from "./../../helpers";

export default {
  name: "Payment",
  components: {
    CreditCard,
    DirectDebit,
  },

  data() {
    return {
      isLoading: false,
    };
  },
  computed: {
    isCreditCard() {
      return this.donation.payementType === "wwf_purchasetokenise";
    },
    isDirectDebit() {
      return this.donation.payementType === "wwf_directdebit";
    },
    isPayPal() {
      return this.donation.payementType === "wwf_paypal";
    },
    ...mapState({
      donation: (state) => state.donation.donation,
      product: (state) => state.product.product,
      supporter: (state) => state.leafRelief.details,
    }),
  },
  props: {
    datalayerName: {
      type: String,
      required: true,
    },
  },

  methods: {
    checkoutPaypal() {
      this.isLoading = true;

      this.$store.dispatch('logFormPayAttempt');
      
      this.$store
        .dispatch("checkoutPaypal")
        .then((response) => {
          window.location.href = response;
        })
        .catch((error) => {
          console.error("Checkout Paypal - Error - ", error);
          this.$store.dispatch("logFormPayFail", "Paypal checkout error");
          this.isLoading = false;
        });
    },
    selectPaymentType(paymentType) {
      // Update donation with the payment type
      this.$store.commit("updateDonation", {
        field: "payementType",
        value: paymentType,
      });
    },
    retry() {
      this.$store
        .dispatch("retryDonation")
        .then((response) => {
          if (this.isCreditCard) {
            this.$refs.creditCardCheckout.reset();
          } else {
            this.$refs.directDebitCheckout.reset();
          }
        })
        .catch((error) => {
          console.error("Retry donation error", error);
        });
    },
    async processSuccess(data) {
      // Make the payment
      if (this.isCreditCard) {
        try {
          // Save the payment type
          await this.$store.dispatch("saveDonation");

          // CREDIT CARD
          await this.$store.dispatch("paymentCreditCard", {
            token: data.token,
            cardHolderName: data.cardHolderName,
          });

          console.log("Credit Card Payment success");
          this.$store.commit("setTransactionId", this.donation.key);
          this.$store.dispatch('logFormPaySuccess', this.donation.key);

          this.$refs.creditCardCheckout.reset();
          this.next();
        } catch (error) {
          console.error("Server Error", error);
          this.$store.dispatch("logFormPayFail", "Server Error");
          this.$refs.creditCardCheckout.reset(
            "Oops something happened and we were not able to process your payment, please try again."
          );
        }
      } else {
        try {
          // Save the payment type
          await this.$store.dispatch("saveDonation");

          // DIRECT DEBIT
          await this.$store.dispatch("paymentDirectDebit", {
            accno: data.accno,
            accrouting: data.accrouting,
            acctitle: data.acctitle,
          });

          console.log("Direct Debit Payment success");
          this.$store.commit("setTransactionId", this.donation.key);
          this.$store.dispatch('logFormPaySuccess', this.donation.key);

          this.$refs.directDebitCheckout.reset();
          this.next();
        } catch (error) {
          console.error("Server Error", error);
          this.$store.dispatch("logFormPayFail", "Server Error");
          this.$refs.directDebitCheckout.reset(
            "Oops something happened and we were not able to process your payment, please try again."
          );
        }
      }
    },
    async next() {
      // Save the signature if needed
      if (this.supporter.agreesDisplayName) {
        await this.$store.dispatch("saveSignature", {
          campaigId: this.datalayerName,
          firstName: this.supporter.firstName,
          lastName: this.supporter.lastName,
          isDonation: true,
        });
      }

      // Send data to Google Tag Manager
      gtm.pushPurchase(
        this.donation.transactionid,
        this.product.productname,
        this.product.id,
        this.product.producttype,
        getNameByDonationType(this.donation.type),
        this.donation.amount,
        this.donation.billing.postcode,
        getNameByPaymentType(this.donation.payementType)
      );

      this.$emit("next");
    },
  },
};
</script>

<style scoped>
.row {
  margin: 0 -15px;
}

.lr-payment {
  padding: var(--lrPadding);
  padding-top: 0;
}

.payment-type-selector {
  margin-top: 0;
  margin-bottom: 2rem;
}

.payment-type-selector .selected {
  color: var(--wwfWhite);
  background-color: var(--wwfRegenerateAustralia);
}

.summary {
  display: flex;
  justify-content: space-between;
}

.summary .amount {
  font-size: 38px;
  line-height: 38px;
  font-family: var(--wwfFont);
  color: var(--wwfRegenerateAustralia);
}

/* SECURE ICON */

.secure img {
  width: 60px;
  opacity: 0.8;
  vertical-align: top;
}

.secure .text {
  display: inline-block;
}

.secure .text p {
  text-transform: uppercase;
  font-weight: 800;
  line-height: normal;
  margin: 0;
  font-size: 12px;
}

.secure .text p:last-child {
  font-weight: 400;
}

.submit button {
  margin-bottom: 0;
}

.paypal-payment {
  display: flex;
  justify-content: center;
  align-content: center;
}

/* PayPal button */

.paypal-button {
  background-color: var(--wwfYellowPaypal) !important;
  height: 50px;
  width: 210px;
  transition: opacity 0.3s;
  color: transparent !important;
  font-family: "Lato";
  font-weight: 600;
}

.paypal-button:before {
  content: "Pay with";
  position: absolute;
  opacity: 0;
  transform: translateY(-50%) translateX(-20px);
}

.paypal-button:after {
  content: "";
  display: inline-block;
  width: 43%;
  height: 0;
  padding-top: 13%;
  background-image: url(https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/2940/dist/assets/img/PayPal.png);
  background-size: 100%;
  background-position: 50%;
  background-repeat: no-repeat;
  right: 0;
  top: 50%;
  position: absolute;
  opacity: 0;
  transform: translateY(-50%);
}

.paypal-button:before {
  content: "Pay with";
  font-size: 13px;
  text-transform: none;
  color: var(--wwfGrey33);
  display: inline-block;
  vertical-align: middle;
  transition: font-size 0s 0s, opacity 0.3s 0.3s, transform 0.3s 0.3s;
  left: 12%;
  top: 50%;
  transform: translateY(-50%) translateX(0px);
  opacity: 1;
  line-height: 0;
}

.paypal-button:after {
  content: "";
  display: inline-block;
  width: 45%;
  height: 0;
  padding-top: 12.8%;
  background-image: url(https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/2940/dist/assets/img/PayPal.png);
  background-size: 100%;
  background-position: 50%;
  background-repeat: no-repeat;
  right: 12%;
  opacity: 1;
  transition: right 0.3s 0.3s, opacity 0.3s 0.3s;
}

@media only screen and (max-width: 64em) {
  .paypal-button:after {
    width: 40%;
    padding-top: 12.8%;
    right: 10%;
  }

  .paypal-button {
    height: 40px;
  }
}

@media only screen and (max-width: 374px) {
  .paypal-button:before {
    content: "Pay with";
    font-size: 11px;
    text-transform: none;
    color: var(--wwfGrey33);
    display: inline-block;
    vertical-align: middle;
    transition: font-size 0s 0s, opacity 0.3s 0.3s, transform 0.3s 0.3s;
    left: 12%;
    top: 50%;
    transform: translateY(-50%) translateX(0px);
    opacity: 1;
  }
}
</style>


// WEBPACK FOOTER //
// Payment.vue?7c1c75e6